import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "../components/Header";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { ArrowLeft, Sparkles, FileText, XCircle, CheckCircle2, Pencil, Phone } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "../lib/api";
import ShareButton from "../components/ShareButton";
import EmbedBadgeSection from "../components/EmbedBadgeSection";
import EditJobDialog from "../components/EditJobDialog";
import useDocumentTitle from "../hooks/useDocumentTitle";

export default function EmployerJobDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  useDocumentTitle(job?.title ? `${job.title} · applicants` : "Job applicants");

  // Compute whether the 7-day edit window is still open.
  const canEdit = useMemo(() => {
    if (!job?.created_at) return false;
    const created = new Date(job.created_at);
    if (Number.isNaN(created.getTime())) return false;
    const days = (Date.now() - created.getTime()) / (1000 * 60 * 60 * 24);
    return days < 7;
  }, [job]);

  useEffect(() => {
    Promise.all([
      api.get(`/jobs/${id}`).then(({ data }) => setJob(data)),
      api.get(`/ai/match/${id}`).then(({ data }) => setCandidates(data || [])).catch(() => setCandidates([])),
    ]).finally(() => setLoading(false));
  }, [id]);

  const downloadResume = async (user_id) => {
    try {
      const res = await api.get(`/resume/${user_id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch (err) {
      console.error("Failed to download resume:", err);
    }
  };

  const toggleJobStatus = async () => {
    if (!job) return;
    const closing = job.is_active;
    if (closing && !window.confirm("Close this job posting? Applicants will no longer be able to apply.")) return;
    try {
      await api.post(`/jobs/${job.job_id}/${closing ? "close" : "reopen"}`);
      toast.success(closing ? "Job closed" : "Job re-opened");
      const { data } = await api.get(`/jobs/${job.job_id}`);
      setJob(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  if (loading) return <div className="min-h-screen"><Header /><div className="p-10">Loading…</div></div>;
  if (!job) return <div className="min-h-screen"><Header /><div className="p-10">Not found</div></div>;

  return (
    <div className="min-h-screen bg-zinc-50">
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link to="/dashboard" className="inline-flex items-center text-sm text-zinc-500 hover:text-zinc-900 mb-6">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to dashboard
        </Link>

        {job.auto_removed && (
          <div
            data-testid="auto-removed-banner"
            className="mb-6 p-4 rounded-md border border-amber-200 bg-amber-50 flex items-start gap-3"
          >
            <span className="h-9 w-9 rounded-md bg-white grid place-items-center text-amber-700 flex-shrink-0 border border-amber-200">
              <XCircle className="h-5 w-5" />
            </span>
            <div className="text-sm">
              <div className="font-semibold text-zinc-950">Auto removed — no activity</div>
              <p className="text-zinc-700 mt-1">
                This posting was automatically closed after {job.auto_removed_at?.slice(0, 10)} because it received no applications in 30 days.
                You can re-open it if you'd like to keep it live.
              </p>
            </div>
          </div>
        )}

        <div className="mb-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-black tracking-tighter text-zinc-950">{job.title}</h1>
            <div className="text-sm text-zinc-500 tag-mono mt-1">
              {job.company_name} · {job.location} · {job.job_type}
              <Badge className={`ml-2 tag-mono ${job.is_active ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-zinc-100 text-zinc-700 border border-zinc-200"}`}>
                {job.is_active ? "active" : "closed"}
              </Badge>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {(job.required_skills || []).map(s => (
                <span key={s} className="tag-mono px-2 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded">{s}</span>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {canEdit && (
              <Button
                variant="outline"
                onClick={() => setEditOpen(true)}
                data-testid="edit-job-btn"
                className="border-blue-200 text-[#002FA7] hover:bg-blue-50"
              >
                <Pencil className="h-4 w-4 mr-2" /> Edit job
              </Button>
            )}
            <Button
              variant={job.is_active ? "outline" : "default"}
              onClick={toggleJobStatus}
              data-testid="toggle-job-status-btn"
              className={job.is_active ? "border-red-200 text-red-700 hover:bg-red-50" : "bg-[#002FA7] hover:bg-[#00227A] text-white"}
            >
              {job.is_active ? <><XCircle className="h-4 w-4 mr-2" /> Close job</> : <><CheckCircle2 className="h-4 w-4 mr-2" /> Re-open job</>}
            </Button>
            <ShareButton
              url={`/jobs/${job.job_id}`}
              title={`${job.title} at ${job.company_name} — apply on QaTestology`}
              testId="emp-job-share-btn"
              label="Share link"
            />
          </div>
        </div>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-[#002FA7]" />
              <h2 className="font-display text-xl font-bold tracking-tight">Ranked candidates</h2>
            </div>
            <span className="ai-score-badge">{candidates.length} applied</span>
          </div>

          {candidates.length === 0 ? (
            <div className="py-10 text-center text-sm text-zinc-500">
              No applicants yet. Share your job link to attract testers.
            </div>
          ) : (
            <div className="divide-y divide-zinc-100">
              {candidates.map((c) => (
                <div key={c.user_id} className="py-5 grid sm:grid-cols-12 gap-4 items-start">
                  <div className="sm:col-span-1">
                    {c.picture ? (
                      <img src={c.picture} alt="" className="h-10 w-10 rounded-full object-cover" />
                    ) : (
                      <div className="h-10 w-10 rounded-full bg-zinc-900 text-white grid place-items-center font-bold">
                        {c.name?.[0]?.toUpperCase() || "U"}
                      </div>
                    )}
                  </div>
                  <div className="sm:col-span-7">
                    <div className="font-semibold text-zinc-950">{c.name || "Candidate"}</div>
                    <div className="text-xs text-zinc-500">{c.profile?.headline || c.profile?.target_role}</div>
                    <div className="text-xs text-zinc-500 tag-mono mt-0.5">
                      {c.profile?.years_experience || 0} yrs · {c.profile?.location || "—"}
                      {user?.is_verified_employer && c.profile?.phone && (
                        <>
                          {" · "}
                          <a
                            href={`tel:${c.profile.phone}`}
                            data-testid={`ranked-candidate-phone-${c.user_id}`}
                            className="inline-flex items-center gap-1 text-[#002FA7] hover:underline"
                          >
                            <Phone className="h-3 w-3" /> {c.profile.phone}
                          </a>
                        </>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {(c.matched_skills || []).map(s => (
                        <span key={s} className="tag-mono px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded text-[10px]">✓ {s}</span>
                      ))}
                      {(c.missing_skills || []).map(s => (
                        <span key={s} className="tag-mono px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded text-[10px]">⚠ {s}</span>
                      ))}
                    </div>
                  </div>
                  <div className="sm:col-span-2 text-center">
                    <div className="ai-score-badge inline-block">{c.match_score} / 100</div>
                    <div className="text-[10px] text-zinc-400 mt-1 uppercase tracking-widest">match</div>
                  </div>
                  <div className="sm:col-span-2 flex flex-col gap-2 items-end">
                    {c.profile?.resume_path && (
                      <Button size="sm" variant="outline" onClick={()=>downloadResume(c.user_id)} data-testid={`resume-btn-${c.user_id}`}>
                        <FileText className="h-3.5 w-3.5 mr-1" /> Resume
                      </Button>
                    )}
                    <Badge className="bg-blue-50 text-blue-700 border border-blue-200">{c.applied_at?.slice(0,10)}</Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
        <EmbedBadgeSection jobId={job.job_id} />
      </main>
      <EditJobDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        job={job}
        onSaved={(updated) => setJob(updated)}
      />
    </div>
  );
}
